import React from "react";
import {
    ExternalLink,
    Heart,
    CheckCircle2,
    Clock,
    XCircle,
    Lightbulb,
    Sparkles,
    Check,
} from "lucide-react";
import { Button } from "views/components/ui/Button";
import { connect } from "react-redux";
import { updateToolLoves } from "actions/tool";
import { withRouter } from "react-router-dom";
import { SocialMediaLinks } from "./SocialMediaLinks";

const ToolDetailsContent = ({ tool, updateToolLoves, userAuth }) => {
    const handleLoveClick = () => {
        console.log("userAuth: ", userAuth);
        if (!userAuth?.isLoggedIn) {
            window.open("/sign-in", "_blank");
            return;
        }
        updateToolLoves(tool.uuid, userAuth.userAccount.uuid);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const renderPricingInfo = () => {
        if (!tool.pricing) return null;

        return (
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Pricing</h3>

                <div className="grid grid-cols-2 gap-4">
                    {tool.pricing.hasFreeTier && (
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <span className="text-gray-700">
                                Free Tier Available
                            </span>
                        </div>
                    )}
                    {tool.pricing.hasTrial && (
                        <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-blue-500" />
                            <span className="text-gray-700">
                                Free Trial Available
                            </span>
                        </div>
                    )}
                </div>

                {tool.pricing.plans && tool.pricing.plans.length > 0 && (
                    <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                            Plans
                        </h4>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {tool.pricing.plans.map((plan, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-4 bg-gray-50"
                                >
                                    <h5 className="font-semibold text-gray-900">
                                        {plan.name}
                                    </h5>
                                    <p className="text-lg font-bold text-gray-900 mt-1">
                                        {plan.price} {tool.pricing.currency}
                                    </p>
                                    {plan.features &&
                                        plan.features.length > 0 && (
                                            <ul className="mt-2 space-y-1">
                                                {plan.features.map(
                                                    (feature, idx) => (
                                                        <li
                                                            key={idx}
                                                            className="flex items-center gap-2 text-sm text-gray-700"
                                                        >
                                                            <Check className="h-4 w-4 text-green-500" />
                                                            {feature}
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderSocialMediaLinks = () => {
        // ... existing code ...
    };

    return (
        <div className="space-y-6">
            {/* Main Content Container */}
            <div className="flex flex-col lg:flex-row gap-8">
                {/* Left Side - Image */}
                <div className="w-full lg:w-2/5">
                    <div className="sticky top-4">
                        <div className="aspect-[3/2] rounded-lg overflow-hidden">
                            <img
                                crossOrigin={
                                    process.env.NODE_ENV === "development"
                                        ? "anonymous"
                                        : ""
                                }
                                src={tool.thumbnail}
                                alt={tool.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>

                {/* Right Side - Content */}
                <div className="w-full lg:w-1/2 space-y-8">
                    <div>
                        {/* Header Info */}
                        <div className="flex items-center justify-between mb-4">
                            <h1 className="text-3xl font-bold">{tool.name}</h1>
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="flex items-center gap-2"
                                    asChild
                                >
                                    <a
                                        href={tool.websiteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        Visit Website
                                    </a>
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="lg"
                                    className="flex items-center gap-2"
                                    onClick={handleLoveClick}
                                >
                                    <Heart className="h-4 w-4" />
                                    {tool.loves}
                                </Button>
                            </div>
                        </div>
                        <p className="text-gray-600 mb-4">{tool.description}</p>
                    </div>
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {tool.tags.map((tag) => (
                            <span
                                key={tag}
                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>

                    {/* Additional Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Category */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-500 mb-1">
                                Category
                            </h3>
                            <p className="text-gray-900">{tool.category}</p>
                        </div>

                        {/* Pricing */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-500 mb-1">
                                Pricing
                            </h3>
                            <p className="text-gray-900">{tool.pricingModel}</p>
                        </div>

                        {/* Last Updated */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-500 mb-1">
                                Last Updated
                            </h3>
                            <p className="text-gray-900">
                                {formatDate(tool.lastUpdated)}
                            </p>
                        </div>
                    </div>

                    {/* Social Media Links */}
                    <div>
                        <SocialMediaLinks
                            socialMediaUrls={tool.socialMediaUrls}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default connect(
    (state) => ({
        userAuth: state.auth.userAuth,
    }),
    { updateToolLoves }
)(withRouter(ToolDetailsContent));
